import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Calendar, Phone, Mail, Home, Heart, FileText, Activity, ChevronDown, ChevronUp, Save, AlertCircle, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientChart = () => {
  const { patientId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [nurseNotes, setNurseNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Section expansion states
  const [expandedSections, setExpandedSections] = useState({
    demographics: true,
    medicalHistory: true,
    vitalSigns: true,
    visits: true,
    followupCall: false,
    nurseNote: false
  });
  
  // Note form states
  const [followupNote, setFollowupNote] = useState('');
  const [nurseNoteContent, setNurseNoteContent] = useState('');
  const [noteType, setNoteType] = useState('Nurse Note');
  const [savingNote, setSavingNote] = useState(false);
  const [noteSuccess, setNoteSuccess] = useState('');

  useEffect(() => {
    if (user) {
      fetchPatientData();
    }
  }, [patientId, user]);

  const fetchPatientData = async () => {
    try {
      setLoading(true);
      const [patientRes, visitsRes, followupsRes, notesRes] = await Promise.all([
        axios.get(`${API}/emr/patients/${patientId}`, { withCredentials: true }),
        axios.get(`${API}/emr/patients/${patientId}/visits`, { withCredentials: true }),
        axios.get(`${API}/emr/patients/${patientId}/followups`, { withCredentials: true }),
        axios.get(`${API}/emr/patients/${patientId}/nurse-notes`, { withCredentials: true })
      ]);
      
      setPatient(patientRes.data);
      setVisits(visitsRes.data);
      setFollowups(followupsRes.data);
      setNurseNotes(notesRes.data);
    } catch (err) {
      setError('Failed to load patient data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSaveFollowupCall = async () => {
    if (!followupNote.trim()) {
      setError('Please enter follow-up notes');
      return;
    }
    
    setSavingNote(true);
    setError('');
    
    try {
      // Get the most recent visit for this patient
      const latestVisit = visits[0];
      
      await axios.post(
        `${API}/emr/followups`,
        {
          patient_id: patientId,
          visit_id: latestVisit?.id || '',
          follow_up_type: 'IV infusion f/u call',
          notes: followupNote,
          contacted: true,
          status: 'completed'
        },
        { withCredentials: true }
      );
      
      setNoteSuccess('Follow-up call documented successfully!');
      setFollowupNote('');
      setTimeout(() => setNoteSuccess(''), 3000);
      fetchPatientData(); // Refresh data
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save follow-up');
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveNurseNote = async () => {
    if (!nurseNoteContent.trim()) {
      setError('Please enter note content');
      return;
    }
    
    setSavingNote(true);
    setError('');
    
    try {
      const latestVisit = visits[0];
      
      await axios.post(
        `${API}/emr/nurse-notes`,
        {
          patient_id: patientId,
          visit_id: latestVisit?.id || null,
          note_type: noteType,
          note_content: nurseNoteContent
        },
        { withCredentials: true }
      );
      
      setNoteSuccess('Nurse note saved successfully!');
      setNurseNoteContent('');
      setTimeout(() => setNoteSuccess(''), 3000);
      fetchPatientData(); // Refresh data
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save nurse note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDownloadEducationPDF = async (visitId) => {
    try {
      const response = await axios.get(
        `${API}/emr/patients/${patientId}/visits/${visitId}/education-pdf`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Patient_Education_${patient.first_name}_${patient.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setNoteSuccess('Patient education PDF downloaded successfully!');
      setTimeout(() => setNoteSuccess(''), 3000);
    } catch (err) {
      setError('Failed to generate PDF. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (!user) {
    navigate('/');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading patient chart...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-xl text-gray-300">Patient not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-yellow-500/20 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/emr')}
            className="flex items-center space-x-2 text-gray-400 hover:text-yellow-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-yellow-400">
                {patient.first_name} {patient.last_name}
              </h1>
              <p className="text-gray-400">Patient Medical Chart</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-400">{error}</p>
          </div>
        )}
        
        {noteSuccess && (
          <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-start space-x-3">
            <Save className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-green-400">{noteSuccess}</p>
          </div>
        )}

        {/* Demographics Section */}
        <section className="mb-6 bg-gray-900 border border-yellow-500/20 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('demographics')}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-800/50 hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <User className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold text-yellow-400">Patient Demographics</h2>
            </div>
            {expandedSections.demographics ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.demographics && (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-400">Full Name</label>
                <p className="text-white font-medium">{patient.first_name} {patient.last_name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" /> Date of Birth
                </label>
                <p className="text-white font-medium">{patient.dob}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 flex items-center">
                  <Phone className="w-4 h-4 mr-1" /> Phone Number
                </label>
                <p className="text-white font-medium">{patient.cell_number}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 flex items-center">
                  <Mail className="w-4 h-4 mr-1" /> Email
                </label>
                <p className="text-white font-medium">{patient.email}</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-gray-400 flex items-center">
                  <Home className="w-4 h-4 mr-1" /> Address
                </label>
                <p className="text-white font-medium">{patient.home_address || 'Not provided'}</p>
              </div>
            </div>
          )}
        </section>

        {/* Medical History Section */}
        <section className="mb-6 bg-gray-900 border border-yellow-500/20 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('medicalHistory')}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-800/50 hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Heart className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold text-yellow-400">Medical History & Assessment</h2>
            </div>
            {expandedSections.medicalHistory ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.medicalHistory && (
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-400 font-medium">Allergies</label>
                <p className="text-white">{patient.allergies || 'None reported'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 font-medium">Current Medications</label>
                <p className="text-white">{patient.current_medications || 'None reported'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 font-medium">Blood Thinners</label>
                <p className="text-white">{patient.anticoagulant ? 'Yes' : 'No'}</p>
              </div>
              
              <div className="border-t border-gray-700 pt-4 mt-4">
                <h3 className="text-lg font-semibold text-yellow-400 mb-3">Physical Assessment - Past Medical History</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 font-medium">Neurological</label>
                    <p className="text-white text-sm">{patient.pmh_neuro || 'No issues noted'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 font-medium">Cardiovascular</label>
                    <p className="text-white text-sm">{patient.pmh_cardio || 'No issues noted'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 font-medium">Pulmonary</label>
                    <p className="text-white text-sm">{patient.pmh_pulmonary || 'No issues noted'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 font-medium">Renal</label>
                    <p className="text-white text-sm">{patient.pmh_renal || 'No issues noted'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-400 font-medium">Gastrointestinal</label>
                    <p className="text-white text-sm">{patient.pmh_gastro || 'No issues noted'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Vital Signs Section */}
        <section className="mb-6 bg-gray-900 border border-yellow-500/20 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('vitalSigns')}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-800/50 hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Activity className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold text-yellow-400">Vital Signs (Initial Intake)</h2>
            </div>
            {expandedSections.vitalSigns ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.vitalSigns && (
            <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <label className="text-sm text-gray-400 font-medium">Blood Pressure</label>
                <p className="text-white text-lg">{patient.blood_pressure || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 font-medium">Temperature</label>
                <p className="text-white text-lg">{patient.temperature || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 font-medium">O2 Saturation</label>
                <p className="text-white text-lg">{patient.oxygen_saturation || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 font-medium">Heart Rate</label>
                <p className="text-white text-lg">{patient.heart_rate || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 font-medium">Respiratory Rate</label>
                <p className="text-white text-lg">{patient.respiratory_rate || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 font-medium">IV Access Location</label>
                <p className="text-white text-lg">{patient.iv_access_location || 'N/A'}</p>
              </div>
            </div>
          )}
        </section>

        {/* Visit History Section */}
        <section className="mb-6 bg-gray-900 border border-yellow-500/20 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('visits')}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-800/50 hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold text-yellow-400">Visit History ({visits.length})</h2>
            </div>
            {expandedSections.visits ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.visits && (
            <div className="p-6">
              {visits.length === 0 ? (
                <p className="text-gray-400 text-center py-4">No visits recorded yet</p>
              ) : (
                <div className="space-y-4">
                  {visits.map((visit) => (
                    <div key={visit.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-yellow-400 font-semibold">{visit.iv_cocktail}</p>
                          <p className="text-sm text-gray-400">
                            {new Date(visit.visit_date).toLocaleDateString()} at {new Date(visit.visit_date).toLocaleTimeString()}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          visit.tolerated_well ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {visit.tolerated_well ? 'Tolerated Well' : 'Issues Reported'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">BP:</span>
                          <span className="text-white ml-2">{visit.blood_pressure || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">HR:</span>
                          <span className="text-white ml-2">{visit.heart_rate || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">RR:</span>
                          <span className="text-white ml-2">{visit.respiratory_rate || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">IV Site:</span>
                          <span className="text-white ml-2">{visit.iv_site || 'N/A'}</span>
                        </div>
                      </div>
                      {visit.add_ons && visit.add_ons.length > 0 && (
                        <div className="mt-2">
                          <span className="text-gray-400 text-sm">Add-ons: </span>
                          <span className="text-white text-sm">{visit.add_ons.join(', ')}</span>
                        </div>
                      )}
                      {visit.nurse_notes && (
                        <div className="mt-2 pt-2 border-t border-gray-700">
                          <p className="text-gray-400 text-sm">Notes:</p>
                          <p className="text-white text-sm">{visit.nurse_notes}</p>
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t border-gray-700 flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                          Documented by: {visit.documented_by_name || 'Unknown'}
                        </div>
                        <button
                          onClick={() => handleDownloadEducationPDF(visit.id)}
                          className="flex items-center space-x-2 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold rounded-lg transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download Patient Education PDF</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* IV Infusion Follow-up Call Section */}
        <section className="mb-6 bg-gray-900 border border-yellow-500/20 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('followupCall')}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-800/50 hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Phone className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold text-yellow-400">IV Infusion F/U Call</h2>
            </div>
            {expandedSections.followupCall ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.followupCall && (
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Document Follow-Up Call
                </label>
                <textarea
                  value={followupNote}
                  onChange={(e) => setFollowupNote(e.target.value)}
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Enter details of follow-up call (patient response, concerns, recommendations...)"
                />
              </div>
              <button
                onClick={handleSaveFollowupCall}
                disabled={savingNote}
                className="px-6 py-2 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{savingNote ? 'Saving...' : 'Save Follow-Up Call'}</span>
              </button>
              
              {/* Previous Follow-ups */}
              {followups.filter(f => f.follow_up_type === 'IV infusion f/u call').length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-3">Previous Follow-Up Calls</h3>
                  <div className="space-y-3">
                    {followups
                      .filter(f => f.follow_up_type === 'IV infusion f/u call')
                      .map((followup) => (
                        <div key={followup.id} className="bg-gray-800 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <p className="text-sm text-gray-400">
                              {new Date(followup.completed_at).toLocaleDateString()} at {new Date(followup.completed_at).toLocaleTimeString()}
                            </p>
                            <span className="text-xs text-green-400">✓ Completed</span>
                          </div>
                          <p className="text-white text-sm">{followup.notes}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            By: {followup.completed_by_name}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Nurse Note Section */}
        <section className="mb-6 bg-gray-900 border border-yellow-500/20 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('nurseNote')}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-800/50 hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold text-yellow-400">Nurse Note</h2>
            </div>
            {expandedSections.nurseNote ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.nurseNote && (
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Note Type
                </label>
                <select
                  value={noteType}
                  onChange={(e) => setNoteType(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                >
                  <option>Nurse Note</option>
                  <option>General Note</option>
                  <option>Assessment Note</option>
                  <option>Care Plan Note</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Note Content (Free Text)
                </label>
                <textarea
                  value={nurseNoteContent}
                  onChange={(e) => setNurseNoteContent(e.target.value)}
                  rows="6"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Enter your nurse note here..."
                />
              </div>
              <button
                onClick={handleSaveNurseNote}
                disabled={savingNote}
                className="px-6 py-2 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{savingNote ? 'Saving...' : 'Save Nurse Note'}</span>
              </button>
              
              {/* Previous Nurse Notes */}
              {nurseNotes.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-3">Previous Nurse Notes</h3>
                  <div className="space-y-3">
                    {nurseNotes.map((note) => (
                      <div key={note.id} className="bg-gray-800 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="text-yellow-400 font-medium text-sm">{note.note_type}</span>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <p className="text-white text-sm whitespace-pre-wrap">{note.note_content}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          By: {note.created_by_name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PatientChart;
